import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { readInstantCache, writeInstantCache } from '../utils/instantCache';
import { toast } from 'sonner';
import { ArrowClockwise, Check, X, ClockCounterClockwise } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Admin section listing every handler-submitted revert request. Admin
 * clicks Approve to actually perform the revert (which also notifies the
 * customer) or Reject to close the request without side-effects.
 *
 * Filter tabs: Pending | Approved | Rejected | All
 */
const RevertRequestsSection = ({ userId }) => {
  const [rows, setRows] = useState(() => readInstantCache('revert_requests', 'all') || []);
  const [loading, setLoading] = useState(() => !readInstantCache('revert_requests', 'all'));
  const [status, setStatus] = useState('pending');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      if (!readInstantCache('revert_requests', 'all')) setLoading(true);
      const { data } = await axios.get(`${API}/revert-requests`, { params: { status } });
      const _v = data.requests || [];
      setRows(_v);
      writeInstantCache('revert_requests', 'all', _v);
    } catch (e) {
      toast.error('Failed to load revert requests');
    } finally { setLoading(false); }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const approve = async (r) => {
    if (!window.confirm(`Approve revert for ${r.customer_username} (₹${r.amount})?\nCustomer will be notified.`)) return;
    setBusyId(r.id);
    try {
      await axios.post(`${API}/revert-requests/${r.id}/approve`, { admin_id: userId });
      toast.success('Approved — entry reverted and customer notified');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Approve failed');
    } finally { setBusyId(null); }
  };

  const reject = async (r) => {
    const why = window.prompt('Reject reason (optional):') ?? '';
    if (why === null) return;
    setBusyId(r.id);
    try {
      await axios.post(`${API}/revert-requests/${r.id}/reject`, { admin_id: userId, reject_reason: why });
      toast.success('Rejected');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reject failed');
    } finally { setBusyId(null); }
  };

  const badge = (s) => {
    if (s === 'pending') return 'bg-amber-100 text-amber-800';
    if (s === 'approved') return 'bg-emerald-100 text-emerald-800';
    if (s === 'rejected') return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4" data-testid="revert-requests-section">
      <div className="flex items-center gap-2 mb-3">
        <ArrowClockwise size={20} weight="bold" className="text-blue-600" />
        <h2 className="text-lg font-bold text-gray-800 flex-1">Revert Requests</h2>
        <button onClick={load} className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200" title="Refresh">↻</button>
      </div>
      <div className="flex gap-2 mb-3 flex-wrap">
        {['pending', 'approved', 'rejected', 'all'].map(s => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            className={`px-3 py-1 rounded-full text-xs font-semibold ${status === s ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
            data-testid={`revert-tab-${s}`}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>
      {loading ? (
        <div className="text-center text-gray-400 py-8 text-sm">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-center text-gray-400 py-8 text-sm flex flex-col items-center gap-2">
          <ClockCounterClockwise size={40} weight="light" />
          <div>No {status === 'all' ? '' : status} revert requests</div>
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map(r => (
            <div
              key={r.id}
              className="border rounded-lg p-3 hover:bg-gray-50"
              data-testid={`revert-row-${r.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-semibold text-gray-800 text-sm">{r.customer_username || '-'}</span>
                    <span className="text-[10px] font-mono px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded">₹{r.amount}</span>
                    {r.utr_number && <span className="text-[10px] font-mono px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded">UTR {r.utr_number}</span>}
                    <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${badge(r.status)}`}>{r.status}</span>
                  </div>
                  <div className="text-[11px] text-gray-600 mt-1">
                    <b>Reason:</b> {r.reason}
                  </div>
                  <div className="text-[10px] text-gray-500 mt-0.5">
                    By: <b>{r.requested_by}</b> ({r.requested_by_role}) · {new Date(r.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                  </div>
                  {r.status === 'approved' && r.approved_by && (
                    <div className="text-[10px] text-emerald-700 mt-0.5">
                      ✓ Approved by {r.approved_by} at {new Date(r.approved_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                    </div>
                  )}
                  {r.status === 'rejected' && r.rejected_by && (
                    <div className="text-[10px] text-red-700 mt-0.5">
                      ✖ Rejected by {r.rejected_by} at {new Date(r.rejected_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                      {r.reject_reason ? ` — ${r.reject_reason}` : ''}
                    </div>
                  )}
                </div>
                {r.status === 'pending' && (
                  <div className="flex flex-col gap-1.5">
                    <button
                      onClick={() => approve(r)}
                      disabled={busyId === r.id}
                      className="flex items-center gap-1 px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold disabled:opacity-50"
                      data-testid={`revert-approve-${r.id}`}
                    >
                      <Check size={12} weight="bold" /> Approve
                    </button>
                    <button
                      onClick={() => reject(r)}
                      disabled={busyId === r.id}
                      className="flex items-center gap-1 px-3 py-1.5 rounded bg-red-600 hover:bg-red-700 text-white text-xs font-semibold disabled:opacity-50"
                      data-testid={`revert-reject-${r.id}`}
                    >
                      <X size={12} weight="bold" /> Reject
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RevertRequestsSection;
